import {
    FormError,
    TableFilterParams,
} from 'app/types/common.types'
import {
    RequestConfigFunction,
} from 'app/types/request.types'
import {
    RequestKeys,
} from 'app/hooks/useRequest'
import {
    FormPart,
} from 'app/types/form.types'
import {
    RichTableColumns,
} from 'app/types/richtable.types'
import {
    BatchEntity,
} from 'app/enums/common.enums'

export enum BulkLoaderType {
    API = 'API',
    CSV = 'CSV',
}

export type SimpleBulkUploadProps<T extends Record<string, any>> = {
    /** Target entity for bulk upload operation */
    entity: BatchEntity
    /** Type of bulk data loader to use. Defaults to API */
    loaderType?: BulkLoaderType
    /** Function that maps record data to upload request configuration */
    uploadParamsMapper: RequestConfigFunction<BatchOperationPayload<T>>
    /** Form config for data update form */
    dataUpdateFormFields: UpdateFormFields<T>
    /** Form config for feedback form */
    feedbackFormFields: FeedbackFormFields<T>
    /** Field name used as unique identifier for records. Defaults to 'id' */
    uniqField?: Extract<keyof T, string>
    /** Additional columns to display in the upload feedback error table */
    errorFeedbackColumns: FailedUploadColumns<T>
    /** Optional custom component to display additional information above the data update form */
    dataUpdateFormInfoComponent?: JSX.Element
    /** Function that returns request configuration for fetching data */
    dataRequest?: RequestConfigFunction,
    /** Unique key identifier for the data request */
    dataRequestKey?: RequestKeys,
    /** Optional filters applied to data request. Defaults to empty object */
    filterParams?: TableFilterParams
    /** Callback function invoked when user clicks DONE or CANCEL buttons */
    onCancel?: () => void
    /** Array of field names that must be present in the CSV file data, used for validation */
    mandatoryCsvFields?: Extract<keyof T, string>[]
    /** Optional callback function invoked when the data update form changes */
    onUpdateFormChange?: (formData: UpdateFormData & T) => void
    /** Values for the data update form (optional), if not provided state handled in component */
    updateFormValue?: UpdateFormData & T
    /** Optional class names for customizing component styles */
    classNames?: {
        dataUpdateForm?: UpdateFormClassNames,
    }
}

export type UpdateFormClassNames = {
    gridCardWrapper?: string,
}

export type UpdateFormData = {
    selectedRecords: number
    [key: string]: any
}

export type FailedUploadColumns<T> = RichTableColumns<keyof (FailedUpload & T)>[]
export type FeedbackFormFields<T> = FormPart<keyof (FeedbackFormData & T)>[]
export type UpdateFormFields<T> = FormPart<keyof (UpdateFormData & T)>[]

export type FailedUpload = {
    errors: FormError[],
    [key: string]: any
}

export type FeedbackFormData = UpdateFormData & {
    updatedRecords: number
    [key: string]: any
}

export type BatchOperationPayload<T> = T & { batchOperationId: number }
